/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import {
    AppSectionAction, ExternalLinkAppSection, InAppSection, AppsPanelSkeleton,
} from '../../stores/skeleton';
import TranslationUtil from '../translation';
import {
    SKELETON_MENU_KEYS, APPS_COMPONENT_ID, LEFT_NAV_KEYS, SKELETON_CTA_KEYS,
} from '../enums';
import Apps from '../../stores/apps';
import AllAppsModelMapper from './appsModelMapper';
import ContentParser from '../contentParser';
import LoggerUtil from '../LoggerUtil';

const COMPONENT_ID = 'SkeletonModelMapper';

const getLocalizedText = (data) => {
    let localizedText = '';
    if (Object.prototype.hasOwnProperty.call(data, 'textId')) {
        localizedText = TranslationUtil.getMessage(data.textId);
    } else if (Object.prototype.hasOwnProperty.call(data, 'text')) {
        localizedText = ContentParser.processText(data.text);
    } else {
        localizedText = '';
    }
    return localizedText;
};

const getSectionAppsData = (sectionData) => {
    const sectionAppsData = {};
    if (!sectionData.appList) return sectionAppsData;
    sectionData.appList.forEach((appCardData) => {
        const {
            sapCode,
            tagLine,
            description,
            appKeyFeatures,
            mediaList,
        } = appCardData;
        sectionAppsData[sapCode] = {
            tagLine,
            description,
            appKeyFeatures,
            mediaList,
        };
    });
    return sectionAppsData;
};

class SkeletonModelMapper {
    /**
     * toAppSection function
     * Map inApp/parsed Contentful data to AppSectionModel
     * @param {object}
     * @return {object}
     */
    toAppSection=(data, onActionEventHandler, onSelectionEventHandler,
        controllerData, onBoardingStatusLight, isPartial = false) => {
        if (Object.prototype.hasOwnProperty.call(data, 'contentType') && data.contentType === 'link') {
            return this.toExternalAppSection(data, onSelectionEventHandler, onBoardingStatusLight);
        }
        return this.toInAppSection(
            data,
            onActionEventHandler,
            onSelectionEventHandler,
            controllerData,
            isPartial
        );
    }

    /**
     * toExternalAppSection function
     * Map inApp/parsed Contentful data to ExternalAppSectionnModel
     * @param {object}
     * @return {object}
     */
    toExternalAppSection=(data, onSelectionEventHandler, onBoardingStatusLight) => {
        let id = data.sys_id;
        if (Object.prototype.hasOwnProperty.call(data, 'id')) {
            ({ id } = data);
        } else if (Object.prototype.hasOwnProperty.call(data, 'inAppID')) {
            id = data.inAppID;
        }
        const linkData = data.link || {};
        let url = linkData.url || '';
        const localizedText = getLocalizedText(data);
        if (linkData.queryParams) {
            url += `?${linkData.queryParams.join('&')}`;
        }

        const targetType = linkData.targetType || '';

        let icon; let iconUrl;
        if (Object.prototype.hasOwnProperty.call(data, 'icon')) {
            icon = (data.icon.spectrumID) ? data.icon.spectrumID : null;
            iconUrl = (data.icon.icon
                    && data.icon.icon.file
                    && data.icon.icon.file.url) ? data.icon.icon.file.url : null;
            if (!iconUrl) {
                iconUrl = (data.icon.icon
                      // eslint-disable-next-line no-underscore-dangle
                      && data.icon.icon._publishUrl) ? data.icon.icon._publishUrl : null;
            }
        }

        const targetClientID = linkData.targetClientID || '';
        const targetScope = linkData.targetScope || '';
        const { ingestId } = linkData;

        return new ExternalLinkAppSection({
            id,
            localizedText,
            url,
            icon,
            iconUrl,
            targetClientID,
            targetScope,
            ingestId,
            type: 'launch-url',
            onSelect: onSelectionEventHandler,
            targetType,
            onBoardingStatusLight,
        });
    }

    // handleMenuItemFilter
    /**
     * toInAppSection function
     * Map inApp/parsed Contentful data to InAppSection Model
     * @param {object}
     * @return {object}
     */
    toInAppSection=(data, onActionEventHandler, onSelectionEventHandler,
        controllerData, isPartial = false) => {
        let subSectionAriaLabel = '';
        let ariaLabel = '';
        let compositeFilter = '';
        const subSectionList = [];
        const actionList = [];
        const hiddenActionList = [];
        const sectionHeaderList = [];
        let sectionAppsData = {};

        const onAction = (onActionEventHandler !== undefined) ? onActionEventHandler
            : () => () => { LoggerUtil.log('dummy callback'); };
        let id = data.sys_id;
        if (Object.prototype.hasOwnProperty.call(data, 'id')) {
            ({ id } = data);
        } else if (Object.prototype.hasOwnProperty.call(data, 'inAppID')) {
            id = data.inAppID;
            LoggerUtil.info(`${APPS_COMPONENT_ID}: ${COMPONENT_ID}: toInAppSection: In-app section ID is "${id}"`);
        }

        const { layoutId } = data;
        const localizedText = getLocalizedText(data);
        let icon;
        let iconUrl;
        if (Object.prototype.hasOwnProperty.call(data, 'icon') && data.icon) {
            icon = (data.icon.spectrumID) ? data.icon.spectrumID : null;
            iconUrl = (data.icon.icon
                    && data.icon.icon.file
                    && data.icon.icon.file.url) ? data.icon.icon.file.url : null;
            if (!iconUrl) {
                iconUrl = (data.icon.icon
                      // eslint-disable-next-line no-underscore-dangle
                      && data.icon.icon._publishUrl) ? data.icon.icon._publishUrl : null;
            }
        }

        if (isPartial === false) {
            if (Object.prototype.hasOwnProperty.call(data, 'compositeFilter')) {
                ({ compositeFilter } = data);
            }

            if (Object.prototype.hasOwnProperty.call(data, 'subSectionAriaLabel')) {
                ({ subSectionAriaLabel } = data);
            }

            if (Object.prototype.hasOwnProperty.call(data, 'subSectionList') && data.subSectionList) {
                Object.keys(data.subSectionList).forEach((key) => {
                    subSectionList.push(
                        this.toAppSection(data.subSectionList[key],
                            onActionEventHandler, onSelectionEventHandler)
                    );
                });
            }
            if (Object.prototype.hasOwnProperty.call(data, 'contextMenuList') && data.contextMenuList) {
                data.contextMenuList.forEach((element) => {
                    const menuItemId = element.id;
                    const actionItem = new AppSectionAction({
                        localizedText: getLocalizedText(element),
                        actionId: element.id,
                        actionType: { content: 'menu' },
                        ariaLabel: element.ariaLabel,
                        disabled: !!(
                            (element.id === SKELETON_MENU_KEYS.CFU_KEY)
                            && controllerData && controllerData.cfuEnabled
                            && (controllerData.cfuEnabled === 'false')),
                    });
                    if (menuItemId !== SKELETON_MENU_KEYS.ENABLE_AU_KEY
                        || (controllerData && controllerData.auEnabled === false)) {
                        actionList.push(actionItem);
                    } else {
                        hiddenActionList.push(actionItem);
                    }
                });
            }

            if (Object.prototype.hasOwnProperty.call(data, 'actionList') && data.actionList) {
                data.actionList.forEach((element) => {
                    const actionItem = new AppSectionAction({
                        localizedText: getLocalizedText(element),
                        actionId: element.id,
                        actionType: { content: 'button' },
                        ariaLabel: element.ariaLabel,
                    });
                    actionList.push(actionItem);
                });
            }
            if (Object.prototype.hasOwnProperty.call(data, 'appList')) {
                // converting appList to object with sapcode as key
                // to avoid looping over array while rendering app card
                sectionAppsData = getSectionAppsData(data);
            }

            if (Object.prototype.hasOwnProperty.call(data, 'headerList') && data.headerList) {
                Object.keys(data.headerList).forEach((key) => {
                    const headerId = data.headerList[key].id;
                    LoggerUtil.info(`${APPS_COMPONENT_ID}: ${COMPONENT_ID}: toInAppSection: Header ID is "${headerId}"`);
                    sectionHeaderList.push({
                        id: headerId,
                        localizedText: getLocalizedText(data.headerList[key]),
                        ariaLabel: data.headerList[key].ariaLabel,
                        layoutId: data.headerList[key].layoutId,
                        includedSapCodeList: data.headerList[key].includedSapCodeList,
                        filter: data.headerList[key].filter,
                        compositeFilter: data.headerList[key].compositeFilter,
                        minimumAppCount: data.headerList[key].minimumAppCount,
                    });
                });
            }
            if (Object.prototype.hasOwnProperty.call(data, 'ariaLabel')) {
                ({ ariaLabel } = data);
            }
        }
        return new InAppSection({
            id,
            layoutId,
            localizedText,
            icon,
            iconUrl,
            subSectionAriaLabel,
            subSectionList,
            actionList,
            hiddenActionList,
            hideBanner: data.hideBanner,
            sectionHeaderList,
            ariaLabel,
            onAction,
            emptyState: data.emptyState,
            sectionAppsData,
            showWait: isPartial,
            compositeFilter,
        });
    }

    getUpdateSection() {
        if (!AppsPanelSkeleton.sectionGroupList.length) {
            return null;
        }
        const appsSectionGroup = AppsPanelSkeleton.sectionGroupList
            .filter(navCategoryItem => (
                navCategoryItem.id === LEFT_NAV_KEYS.APPS_SECTION_GROUP_KEY));
        if (!appsSectionGroup.length) {
            LoggerUtil.info(`${COMPONENT_ID}: setUpdatesCount: apps section group not found`);
            return null;
        }
        const updatesSection = appsSectionGroup[0].appSections
            .filter(appSection => appSection.id === LEFT_NAV_KEYS.UPDATES_KEY);
        if (!updatesSection.length) {
            LoggerUtil.info(`${COMPONENT_ID}: setUpdatesCount: updatesSection not found`);
            return null;
        }
        return updatesSection[0];
    }

    setUpdatesCount() {
        const availableUpdateCount = AllAppsModelMapper.getUpdatesCount(Apps.apps);
        const updatesSection = this.getUpdateSection();
        if (updatesSection) {
            updatesSection.showWaitIcon(false);
            updatesSection.updateCount(availableUpdateCount);
        }
    }

    showWaitIconForUpdates(showWait) {
        const updatesSection = this.getUpdateSection();
        if (updatesSection) {
            updatesSection.showWaitIcon(showWait);
        }
    }

    setWamFailState(failState) {
        const updatesSection = this.getUpdateSection();
        if (updatesSection) {
            updatesSection.setFailState(failState);
        }
    }

    setUpdateAllState(isUpdateAllProcessing) {
        if (AllAppsModelMapper.getUpdatesCount(Apps.apps) === 0
            || isUpdateAllProcessing === true) {
            AppsPanelSkeleton.disableActionItem(
                LEFT_NAV_KEYS.APPS_SECTION_GROUP_KEY,
                LEFT_NAV_KEYS.UPDATES_KEY,
                SKELETON_CTA_KEYS.UPDATE_ALL_KEY
            );
        } else {
            AppsPanelSkeleton.enableActionItem(
                LEFT_NAV_KEYS.APPS_SECTION_GROUP_KEY,
                LEFT_NAV_KEYS.UPDATES_KEY,
                SKELETON_CTA_KEYS.UPDATE_ALL_KEY
            );
        }
    }

    updateSkeletonStateFromAppsList(processingStateData) {
        this.setUpdatesCount();
        this.setUpdateAllState(
            processingStateData ? processingStateData.isUpdateAllProcessing : null
        );
    }
}
export default new SkeletonModelMapper();
